import {
  documentBlockSchema,
  type DocumentBlockDTO,
  type DocumentEditOperationDTO,
  type DocumentThemeDTO,
  type DocumentThemePresetDTO,
} from '@shared/schemas/document-model';
import { isInlineEditableBlock } from './block-text';
import { htmlToInlineText } from './html-to-inline';

export type UpdateBlockResult =
  | { ok: true; operation: Extract<DocumentEditOperationDTO, { op: 'update_block' }> }
  | { ok: false; reason: 'empty' | 'invalid' | 'unchanged' | 'unsupported' };

/**
 * Construit `update_block` à partir du bloc courant du modèle et du HTML édité dans
 * l'aperçu : seul le texte change, le reste du bloc (style, titre, attribution…) est conservé.
 */
export function buildUpdateBlockOperation(input: {
  block: DocumentBlockDTO | null | undefined;
  html: string;
  sectionId: string;
}): UpdateBlockResult {
  const { block } = input;
  if (!isInlineEditableBlock(block)) {
    return { ok: false, reason: 'unsupported' };
  }
  const text = htmlToInlineText(input.html);
  if (!text) {
    return { ok: false, reason: 'empty' };
  }
  if (text === block.text) {
    return { ok: false, reason: 'unchanged' };
  }
  const parsed = documentBlockSchema.safeParse({ ...block, text });
  if (!parsed.success) {
    return { ok: false, reason: 'invalid' };
  }
  return { ok: true, operation: { block: parsed.data, op: 'update_block', sectionId: input.sectionId } };
}

function cleanInstruction(value: string | undefined) {
  const trimmed = value?.trim();
  return trimmed ? trimmed.slice(0, 2_000) : undefined;
}

/** Sans consigne, `rewrite_section` régénère la section à partir de son rôle éditorial. */
export function buildRewriteSectionOperation(sectionId: string, instruction?: string): DocumentEditOperationDTO {
  const cleaned = cleanInstruction(instruction);
  return cleaned
    ? { instruction: cleaned, op: 'rewrite_section', sectionId }
    : { op: 'rewrite_section', sectionId };
}

export function buildRewriteBlockOperation(input: {
  blockId: string;
  instruction: string;
  sectionId: string;
}): DocumentEditOperationDTO | null {
  const instruction = cleanInstruction(input.instruction);
  return instruction
    ? { blockId: input.blockId, instruction, op: 'rewrite_block', sectionId: input.sectionId }
    : null;
}

export function buildInsertSectionOperation(input: {
  afterSectionId: string | null;
  instruction?: string;
  title: string;
}): DocumentEditOperationDTO | null {
  const title = input.title.trim().slice(0, 200);
  if (!title) {
    return null;
  }
  return {
    afterSectionId: input.afterSectionId,
    instruction: cleanInstruction(input.instruction) ?? `Rédige le contenu de la section « ${title} ».`,
    op: 'insert_section',
    title,
  };
}

/** Un preset choisi remplace aussi les couleurs imposées, sinon le changement serait invisible. */
export function buildThemeOperation(current: DocumentThemeDTO, preset: DocumentThemePresetDTO): DocumentEditOperationDTO {
  return { op: 'set_theme', theme: { density: current.density, preset } };
}
